import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  CContainer, CCard, CCardBody, CCardHeader, CButton,
  CNav, CNavItem, CNavLink, CTabContent, CTabPane
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilArrowLeft, cilBell, cilSettings } from '@coreui/icons';

import AlertsHistory from './AlertsHistory';
import AlertsSettings from './AlertsSettings';

const INITIAL_CONFIG = {
  send_email: false, emails: '', send_telegram: false, telegram_chat_id: '',
  send_max: false, max_chat_id: '', send_daily_report: false, send_weekly_report: false,
  send_monthly_report: false, send_shift_report: false, daily_report_time: '',
  weekly_report_time: '', monthly_report_time: '', report_day_of_week: 0,
  report_day_of_month: 1, report_all_devices: true, report_by_zones: false,
  send_shift_wait_data: false,
  report_devices: [], report_zones: [],
  alert_battery: true, alert_temp: true, alert_static: true, alert_dark: true,
  alert_ai_frozen: true, alert_idle_truck: true, alert_power_off: true,
  enable_prediction: false, prediction_targets: {},
};

const AlertsPage = () => {
  const isStaff = localStorage.getItem('is_staff') === 'true';

  const [activeAlertTab, setActiveAlertTab] = useState('history');
  const [orgAlerts, setOrgAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(true);

  const [devices, setDevices] = useState([]);
  const [workZones, setWorkZones] = useState([]);
  const [savingNotif, setSavingNotif] = useState(false);

  const [notifConfig, setNotifConfig] = useState(INITIAL_CONFIG);

  useEffect(() => {
    const fetchInitialData = async () => {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      try {
        const resAlerts = await axios.get('/api/organization/alerts/', { headers });
        setOrgAlerts(resAlerts.data);
      } catch (err) {
        console.error('Ошибка загрузки инцидентов:', err);
      } finally {
        setLoadingAlerts(false);
      }

      if (isStaff) {
        try {
          const resConfig = await axios.get('/api/user/notification-config/', { headers });
          const configData = resConfig.data;
          if (!configData.prediction_targets) configData.prediction_targets = {};
          
          setNotifConfig((prev) => ({ ...prev, ...configData }));

          const [resDevices, resZones] = await Promise.all([
            axios.get('/api/devices/', { headers }),
            axios.get('/api/work-zones/', { headers }),
          ]);

          setDevices(resDevices.data.results || resDevices.data || []);
          setWorkZones(resZones.data || []);
        } catch (err) {
          console.error('Ошибка загрузки настроек:', err);
        }
      }
    };
    fetchInitialData();
  }, [isStaff]);

  const handleSaveNotifConfig = async () => {
    setSavingNotif(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { ...notifConfig };

      if (!payload.daily_report_time) payload.daily_report_time = null;
      if (!payload.weekly_report_time) payload.weekly_report_time = null;
      if (!payload.monthly_report_time) payload.monthly_report_time = null;

      await axios.put('/api/user/notification-config/', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Настройки успешно сохранены!');
    } catch (err) {
      toast.error('Ошибка сохранения настроек.');
    } finally {
      setSavingNotif(false);
    }
  };

  const getTabStyle = (tabName) => ({
    cursor: 'pointer',
    border: 'none',
    borderBottom: activeAlertTab === tabName ? '2px solid #321fdb' : '2px solid transparent',
    color: activeAlertTab === tabName ? '#321fdb' : '#6c757d',
    fontWeight: 'bold',
  });

  return (
    <div className="bg-light min-vh-100 p-3 p-md-4">
      <CContainer fluid>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
          <h2>Уведомления и Инциденты</h2>
          <CButton color="secondary" as={Link} to="/dashboard" size="sm">
            <CIcon icon={cilArrowLeft} className="me-2" /> Назад
          </CButton>
        </div>

        <CCard className="shadow-sm border-0">
          <CCardHeader className="bg-white border-bottom-0 pb-0">
            <CNav variant="tabs" className="w-100 border-bottom">
              <CNavItem>
                <CNavLink
                  active={activeAlertTab === 'history'}
                  onClick={() => setActiveAlertTab('history')}
                  style={getTabStyle('history')}
                >
                  <CIcon icon={cilBell} className="me-2" /> История инцидентов
                </CNavLink>
              </CNavItem>
              {isStaff && (
                <CNavItem>
                  <CNavLink
                    active={activeAlertTab === 'settings'}
                    onClick={() => setActiveAlertTab('settings')}
                    style={getTabStyle('settings')}
                  >
                    <CIcon icon={cilSettings} className="me-2" /> Настройки рассылки
                  </CNavLink>
                </CNavItem>
              )}
            </CNav>
          </CCardHeader>

          <CCardBody className="p-0 bg-light" style={{ minHeight: '65vh' }}>
            <CTabContent>
              <CTabPane visible={activeAlertTab === 'history'} className="p-3">
                <AlertsHistory loading={loadingAlerts} alerts={orgAlerts} />
              </CTabPane>

              {isStaff && (
                <CTabPane visible={activeAlertTab === 'settings'} className="p-3">
                  <AlertsSettings
                    config={notifConfig}
                    setConfig={setNotifConfig}
                    devices={devices}
                    workZones={workZones}
                    onSave={handleSaveNotifConfig}
                    saving={savingNotif}
                  />
                </CTabPane>
              )}
            </CTabContent>
          </CCardBody>
        </CCard>
      </CContainer>
    </div>
  );
};

export default AlertsPage;